import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "../../components/DashboardLayout";
import api from "../../lib/api";
import {
  Building2, BarChart3, Users, Wallet, ShieldCheck, FileSearch,
  Clock, FileBarChart, Settings,
} from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from "recharts";
import { ClinicStatusBadge, RenewalBadge, PaymentStatusBadge } from "./components/ClinicBadges";

export const links = [
  { to: "/super-admin", label: "Overview", icon: <BarChart3 className="w-4 h-4" />, end: true },
  { to: "/super-admin/clinics", label: "Clinics", icon: <Building2 className="w-4 h-4" /> },
  { to: "/super-admin/pharmacies", label: "Pharmacies", icon: <Building2 className="w-4 h-4" /> },
  { to: "/super-admin/applications", label: "Applications", icon: <FileSearch className="w-4 h-4" /> },
  { to: "/super-admin/users", label: "Platform Users", icon: <Users className="w-4 h-4" /> },
  { to: "/super-admin/patients", label: "Patients", icon: <Users className="w-4 h-4" /> },
  { to: "/super-admin/encounters", label: "Encounters", icon: <FileSearch className="w-4 h-4" /> },
  { to: "/super-admin/platform-billing", label: "Renewals & Payments", icon: <Wallet className="w-4 h-4" /> },
  { to: "/super-admin/audit", label: "Audit Logs", icon: <FileSearch className="w-4 h-4" /> },
  { to: "/super-admin/webhooks", label: "Webhook Events", icon: <Clock className="w-4 h-4" /> },
  { to: "/super-admin/reports", label: "Reports", icon: <FileBarChart className="w-4 h-4" /> },
  { to: "/super-admin/settings", label: "Settings", icon: <Settings className="w-4 h-4" /> },
];

const CHART_COLORS = ["#0EA5A4", "#0F4C5C", "#F4B400", "#E67E22", "#9333EA", "#EF4444"];

export function SuperAdminOverview() {
  const [stats, setStats] = useState(null);
  const [renewals, setRenewals] = useState([]);
  const [renewalWindow, setRenewalWindow] = useState("30");
  const [clinicsGrowth, setClinicsGrowth] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [planDist, setPlanDist] = useState([]);
  const [statusDist, setStatusDist] = useState([]);
  const [apptVolume, setApptVolume] = useState([]);

  useEffect(() => {
    api.get("/admin/dashboard-stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/charts/clinics-growth?months=12").then((r) => setClinicsGrowth(r.data || [])).catch(() => {});
    api.get("/admin/charts/revenue-trend?months=12").then((r) => setRevenue(r.data || [])).catch(() => {});
    api.get("/admin/charts/plan-distribution").then((r) => setPlanDist(r.data || [])).catch(() => {});
    api.get("/admin/charts/status-distribution").then((r) => setStatusDist(r.data || [])).catch(() => {});
    api.get("/admin/charts/appointment-volume?months=12").then((r) => setApptVolume(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/admin/renewals?window=${renewalWindow}`).then((r) => setRenewals(r.data?.rows || [])).catch(() => {});
  }, [renewalWindow]);

  return (
    <DashboardLayout links={links} title="Platform Overview">
      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="overview-stats">
        <Metric label="MRR" value={`₹${(stats?.revenue?.mrr || 0).toLocaleString()}`} tone="medical" testid="metric-mrr" />
        <Metric label="Revenue (mo)" value={`₹${(stats?.revenue?.month || 0).toLocaleString()}`} testid="metric-revenue-month" />
        <Metric label="Total clinics" value={stats?.clinics?.total || 0} testid="metric-clinic-total" />
        <Metric label="Active" value={stats?.clinics?.active || 0} tone="ok" testid="metric-clinic-active" />
        <Metric label="Trial" value={stats?.clinics?.trial || 0} tone="warn" testid="metric-clinic-trial" />
        <Metric label="Suspended" value={stats?.clinics?.suspended || 0} tone="warn" testid="metric-clinic-suspended" />
        <Metric label="Disabled" value={stats?.clinics?.disabled || 0} tone="danger" testid="metric-clinic-disabled" />
        <Metric label="Users" value={stats?.users?.total || 0} testid="metric-user-total" />
        <Metric label="Active (30d)" value={stats?.users?.active_30d || 0} tone="ok" testid="metric-user-active30" />
        <Metric label="Inactive" value={stats?.users?.inactive || 0} tone="warn" testid="metric-user-inactive" />
        <Metric label="Doctors" value={stats?.users?.doctors || 0} testid="metric-doctors" />
        <Metric label="Patients" value={stats?.patients?.total || 0} testid="metric-patients" />
        <Metric label="Appointments" value={stats?.appointments?.total || 0} testid="metric-appts" />
        <Metric label="Renewals 30d" value={stats?.renewals?.upcoming_30d || 0} tone="warn" testid="metric-renewals" />
        <Metric label="Overdue" value={stats?.renewals?.overdue || 0} tone="danger" testid="metric-overdue" />
        <Metric label="Trial ending 7d" value={stats?.renewals?.trials_ending_7d || 0} tone="warn" testid="metric-trials-ending" />
        <Metric label="AI clinics" value={stats?.ai?.enabled_clinics || 0} testid="metric-ai-clinics" />
        <Metric label="Tokens (mo)" value={(stats?.ai?.tokens_month || 0).toLocaleString()} testid="metric-tokens" />
      </div>

      {/* Renewals widget */}
      <div className="mg-card mt-6" data-testid="renewals-widget">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="font-bold text-mg-navy text-base inline-flex items-center gap-2">
              <Clock className="w-4 h-4 text-mg-medical" /> Upcoming Renewals
            </h3>
            <p className="text-xs text-slate-500">SaaS subscriptions reaching renewal in the selected window.</p>
          </div>
          <div className="flex gap-2 flex-wrap" data-testid="renewal-window-tabs">
            {[
              { v: "7", label: "Next 7d" },
              { v: "15", label: "Next 15d" },
              { v: "30", label: "Next 30d" },
              { v: "overdue", label: "Overdue" },
              { v: "trial_ending", label: "Trial ending" },
            ].map((opt) => (
              <button key={opt.v} onClick={() => setRenewalWindow(opt.v)}
                className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border transition ${renewalWindow === opt.v ? "bg-mg-medical text-white border-mg-medical" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`renewal-win-${opt.v}`}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="renewals-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
              <tr><th className="py-2">Clinic</th><th>Plan</th><th>Status</th><th>Renewal</th><th>Trial</th><th className="text-right">Amount</th><th className="text-right">Action</th></tr>
            </thead>
            <tbody>
              {renewals.length === 0 && (
                <tr><td colSpan="7" className="py-8 text-center text-slate-400 text-sm">No renewals in this window.</td></tr>
              )}
              {renewals.map((r) => (
                <tr key={r.clinic_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="py-2">
                    <Link to={`/super-admin/clinics/${r.clinic_id}`} className="font-semibold text-mg-navy hover:text-mg-medical">{r.clinic_name}</Link>
                    <div className="text-[10px] text-slate-500 font-mono">/{r.clinic_slug}</div>
                  </td>
                  <td className="text-xs">{r.plan_name || "—"}</td>
                  <td><ClinicStatusBadge status={r.subscription_status} trial={r.is_trial} trialDays={r.trial_remaining_days} /></td>
                  <td><RenewalBadge days={r.days_to_renewal} /></td>
                  <td>{r.is_trial ? <RenewalBadge days={r.days_to_trial_end} isTrial /> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="text-right text-xs font-semibold">₹{(r.amount || 0).toLocaleString()}<PaymentStatusBadge status={r.payment_status} /></td>
                  <td className="text-right">
                    <Link to={`/super-admin/clinics/${r.clinic_id}/subscription`} className="text-xs text-mg-medical font-semibold hover:underline">Change Plan</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <ChartCard title="Clinics growth" subtitle="New clinics per month" testid="chart-clinics-growth">
          {clinicsGrowth.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={clinicsGrowth}>
                <CartesianGrid stroke="#f1f5f9" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Line type="monotone" dataKey="new_clinics" stroke="#0EA5A4" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
        <ChartCard title="Revenue trend" subtitle="Paid invoice revenue by month" testid="chart-revenue">
          {revenue.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={revenue}>
                <CartesianGrid stroke="#f1f5f9" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Bar dataKey="revenue" fill="#F4B400" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
        <ChartCard title="Subscription status distribution" subtitle="Active / Trial / Suspended / etc" testid="chart-status">
          {statusDist.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusDist} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={80} label>
                  {statusDist.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
        <ChartCard title="Plan distribution" subtitle="Clinics per plan" testid="chart-plans">
          {planDist.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={planDist} layout="vertical">
                <CartesianGrid stroke="#f1f5f9" />
                <XAxis type="number" fontSize={10} />
                <YAxis type="category" dataKey="name" fontSize={10} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="#0F4C5C" />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
        <ChartCard title="Appointment volume" subtitle="Appointments per month" testid="chart-appts" full>
          {apptVolume.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={apptVolume}>
                <CartesianGrid stroke="#f1f5f9" />
                <XAxis dataKey="label" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip />
                <Line type="monotone" dataKey="appointments" stroke="#9333EA" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty />}
        </ChartCard>
      </div>
    </DashboardLayout>
  );
}

function Metric({ label, value, tone = "default", testid }) {
  const tones = {
    default: "bg-white border-slate-100",
    ok: "bg-green-50 border-green-100",
    warn: "bg-amber-50 border-amber-100",
    danger: "bg-red-50 border-red-100",
    medical: "bg-mg-medical/5 border-mg-medical/20",
  };
  return (
    <div className={`rounded-2xl border p-3 ${tones[tone] || tones.default}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 truncate">{label}</div>
      <div className="text-xl font-extrabold text-mg-navy mt-1 truncate">{value}</div>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testid, full }) {
  return (
    <div className={`mg-card ${full ? "lg:col-span-2" : ""}`} data-testid={testid}>
      <h3 className="font-bold text-mg-navy text-base">{title}</h3>
      <p className="text-xs text-slate-500 mb-3">{subtitle}</p>
      {children}
    </div>
  );
}

function Empty() {
  return <div className="h-[220px] flex items-center justify-center text-xs text-slate-400">No data yet.</div>;
}

export function SuperAdminPlans() {
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/admin/plans").then((r) => setPlans(r.data)); }, []);
  return (
    <DashboardLayout links={links} title="Pricing Plans">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {plans.map((p) => (
          <div key={p.plan_id} className="mg-card" data-testid={`plan-${p.name.toLowerCase()}`}>
            <h3 className="font-extrabold text-mg-navy text-lg">{p.name}</h3>
            <div className="text-3xl font-extrabold text-mg-medical mt-2">₹{p.price}<span className="text-sm text-slate-500 font-medium">/{p.billing_cycle.slice(0,2)}</span></div>
            <ul className="mt-4 space-y-1.5 text-sm">
              {(p.features || []).map((f, i) => <li key={i} className="text-slate-600">• {f}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

export function SuperAdminAudit() {
  const [activeTab, setActiveTab] = useState("audit");
  const [data, setData] = useState({ rows: [], total: 0 });
  const [meta, setMeta] = useState({ modules: [], entity_types: [], actions: [] });
  const [filters, setFilters] = useState({
    q: "", action: "", module: "", entity_type: "", entity_id: "",
    actor_user_id: "", clinic_id: "", date_from: "", date_to: "",
    severity: "", ip_address: "", status: "",
  });
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const limit = 50;

  const load = useCallback(async (overrideSkip) => {
    setLoading(true);
    try {
      const s = overrideSkip ?? 0;
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      params.append("limit", String(limit));
      params.append("skip", String(s));
      const r = await api.get(`/admin/audit-logs?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  }, [filters, limit]);

  useEffect(() => {
    api.get("/admin/audit-logs/meta/filters").then((r) => setMeta(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(0); setSkip(0); }, [filters, load]);

  const loadDiagnostics = useCallback(async () => {
    setDiagLoading(true);
    try {
      const r = await api.get("/admin/auth/diagnostics");
      setDiagData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load diagnostics");
    }
    setDiagLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === "diagnostics" && !diagData && !diagLoading) {
      loadDiagnostics();
    }
  }, [activeTab, diagData, diagLoading, loadDiagnostics]);

  const clear = () => setFilters({
    q: "", action: "", module: "", entity_type: "", entity_id: "",
    actor_user_id: "", clinic_id: "", date_from: "", date_to: "",
    severity: "", ip_address: "", status: "",
  });
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const activeCount = Object.values(filters).filter(Boolean).length;

  const exportCsv = () => {
    const rows = data.rows.map((l) => ({
      time: l.created_at, action: l.action, module: (l.action || "").split("_")[0],
      actor: l.actor?.email || l.actor_user_id || "", clinic: l.clinic?.name || "",
      entity_type: l.entity_type || "", entity_id: l.entity_id || "",
      description: l.description || "", ip: l.ip_address || "",
    }));
    if (!rows.length) return toast.error("No data");
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
      return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
    };
    const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `medigenie-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <DashboardLayout links={links} title="Audit Logs">
      <div className="mb-4 border-b border-slate-200 flex items-center gap-2" data-testid="audit-page-tabs">
        <button
          onClick={() => setActiveTab("audit")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "audit" ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}
          data-testid="audit-tab-events"
        >
          Audit Events
        </button>
        <button
          onClick={() => setActiveTab("diagnostics")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${activeTab === "diagnostics" ? "border-mg-medical text-mg-medical" : "border-transparent text-slate-500 hover:text-mg-navy"}`}
          data-testid="audit-tab-auth-diagnostics"
        >
          Auth Diagnostics
        </button>
      </div>

      {activeTab === "diagnostics" ? (
        <AuthDiagnosticsPanel
          data={diagData}
          loading={diagLoading}
          onRefresh={loadDiagnostics}
        />
      ) : (
        <>
      {/* Filter bar */}
      <div className="mg-card mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <input className="mg-input text-sm" placeholder="Keyword (action / description / entity)…"
            value={filters.q} onChange={(e) => setF("q", e.target.value)} data-testid="audit-search" />
          <select className="mg-input text-sm" value={filters.module} onChange={(e) => setF("module", e.target.value)} data-testid="audit-module-filter">
            <option value="">All modules</option>
            {meta.modules.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="mg-input text-sm" value={filters.action} onChange={(e) => setF("action", e.target.value)} data-testid="audit-action-filter">
            <option value="">All actions</option>
            {meta.actions.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          <select className="mg-input text-sm" value={filters.entity_type} onChange={(e) => setF("entity_type", e.target.value)} data-testid="audit-entity-filter">
            <option value="">All entities</option>
            {meta.entity_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <input className="mg-input text-sm" type="date"
            value={filters.date_from?.slice(0, 10) || ""}
            onChange={(e) => setF("date_from", e.target.value ? new Date(e.target.value).toISOString() : "")}
            data-testid="audit-date-from" />
          <input className="mg-input text-sm" type="date"
            value={filters.date_to?.slice(0, 10) || ""}
            onChange={(e) => setF("date_to", e.target.value ? new Date(e.target.value + "T23:59:59").toISOString() : "")}
            data-testid="audit-date-to" />
          <input className="mg-input text-sm" placeholder="Actor user_id" value={filters.actor_user_id}
            onChange={(e) => setF("actor_user_id", e.target.value)} data-testid="audit-actor-filter" />
          <input className="mg-input text-sm" placeholder="Clinic id" value={filters.clinic_id}
            onChange={(e) => setF("clinic_id", e.target.value)} data-testid="audit-clinic-filter" />
          <input className="mg-input text-sm" placeholder="Entity id" value={filters.entity_id}
            onChange={(e) => setF("entity_id", e.target.value)} data-testid="audit-entity-id-filter" />
          <input className="mg-input text-sm" placeholder="IP address" value={filters.ip_address}
            onChange={(e) => setF("ip_address", e.target.value)} data-testid="audit-ip-filter" />
          <input className="mg-input text-sm" placeholder="Severity" value={filters.severity}
            onChange={(e) => setF("severity", e.target.value)} data-testid="audit-severity-filter" />
          <div className="flex items-center gap-2">
            {activeCount > 0 && (
              <button onClick={clear} className="text-xs text-mg-medical font-semibold" data-testid="audit-clear-filters">Clear ({activeCount})</button>
            )}
            <button onClick={exportCsv} className="mg-btn-secondary text-xs ml-auto" data-testid="audit-export-csv">Export</button>
          </div>
        </div>
      </div>

      <div className="text-xs text-slate-500 mb-2">{loading ? "Loading…" : `${data.rows.length} of ${data.total}`}</div>

      <div className="mg-card overflow-x-auto p-0">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
            <tr><th className="px-3 py-3">Time</th><th>Actor</th><th>Action</th><th>Module</th><th>Entity</th><th>Clinic</th><th>Description</th><th>IP</th><th></th></tr>
          </thead>
          <tbody>
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan="9" className="px-3 py-8 text-center text-slate-400">No audit logs match.</td></tr>
            )}
            {data.rows.map((l) => (
              <tr key={l.log_id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`log-${l.log_id}`}>
                <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                <td className="text-xs">
                  {l.actor ? (
                    <>
                      <div className="font-semibold text-mg-navy">{l.actor.name || l.actor.email}</div>
                      <div className="text-[10px] text-slate-400">{l.actor.role}</div>
                    </>
                  ) : <span className="font-mono">{l.actor_user_id?.slice(-8) || "—"}</span>}
                </td>
                <td className="text-xs"><code className="text-mg-medical font-mono">{l.action}</code></td>
                <td className="text-[10px] uppercase font-bold text-slate-500">{(l.action || "").split("_")[0] || "—"}</td>
                <td className="text-xs">{l.entity_type || "—"}</td>
                <td className="text-xs">{l.clinic?.name || "—"}</td>
                <td className="text-xs text-slate-600">{l.description || "—"}</td>
                <td className="text-[10px] text-slate-400 font-mono">{l.ip_address || "—"}</td>
                <td className="text-right">
                  <button onClick={() => setSelectedLog(l)} className="text-xs text-mg-medical font-semibold" data-testid={`view-log-${l.log_id}`}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-3 text-sm">
        <button disabled={skip <= 0} onClick={() => { const s = Math.max(0, skip - limit); setSkip(s); load(s); }}
          className="mg-btn-secondary text-xs disabled:opacity-50" data-testid="audit-prev-page">Prev</button>
        <span className="text-xs text-slate-500">Page {Math.floor(skip / limit) + 1} of {Math.max(1, Math.ceil(data.total / limit))}</span>
        <button disabled={skip + limit >= data.total} onClick={() => { const s = skip + limit; setSkip(s); load(s); }}
          className="mg-btn-secondary text-xs disabled:opacity-50" data-testid="audit-next-page">Next</button>
      </div>

      {selectedLog && (
        <AuditLogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}

        </>
      )}
    </DashboardLayout>
  );
}

function AuthDiagnosticsPanel({ data, loading, onRefresh }) {
  return (
    <div className="space-y-4" data-testid="auth-diagnostics-panel">
      <div className="mg-card flex items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-mg-navy text-base" data-testid="auth-diagnostics-title">Session & Token Health</h3>
          <p className="text-xs text-slate-500">Read-only auth diagnostics for incident debugging.</p>
        </div>
        <button onClick={onRefresh} className="mg-btn-secondary text-xs" data-testid="auth-diagnostics-refresh-btn">Refresh</button>
      </div>

      {loading && <div className="mg-card text-sm text-slate-500" data-testid="auth-diagnostics-loading">Loading diagnostics…</div>}

      {!loading && !data && (
        <div className="mg-card text-sm text-slate-500" data-testid="auth-diagnostics-empty">No diagnostics available.</div>
      )}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mg-card" data-testid="auth-diagnostics-token-health">
              <h4 className="font-bold text-mg-navy mb-3">Token Transport</h4>
              <div className="space-y-2 text-sm">
                <DiagRow label="Overall" value={data.overall_status || "unknown"} />
                <DiagRow label="Detected transport" value={data.token_health?.transport_detected || "none"} />
                <DiagRow label="Bearer present" value={String(Boolean(data.token_health?.bearer_present))} />
                <DiagRow label="Bearer valid" value={String(Boolean(data.token_health?.bearer_valid))} />
                <DiagRow label="Cookie present" value={String(Boolean(data.token_health?.cookie_present))} />
                <DiagRow label="Cookie valid" value={String(Boolean(data.token_health?.cookie_valid))} />
                <DiagRow label="Cookie expires" value={data.token_health?.cookie_expires_at || "—"} />
              </div>
            </div>

            <div className="mg-card" data-testid="auth-diagnostics-me-snapshot">
              <h4 className="font-bold text-mg-navy mb-3">Current /auth/me Snapshot</h4>
              <div className="space-y-2 text-sm">
                <DiagRow label="User ID" value={data.me_snapshot?.user_id || "—"} />
                <DiagRow label="Email" value={data.me_snapshot?.email || "—"} />
                <DiagRow label="Role" value={data.me_snapshot?.role || "—"} />
                <DiagRow label="Clinic ID" value={data.me_snapshot?.clinic_id || "—"} />
                <DiagRow label="Checked at" value={data.checked_at || "—"} />
              </div>
            </div>
          </div>

          <div className="mg-card" data-testid="auth-diagnostics-recent-events">
            <h4 className="font-bold text-mg-navy mb-3">Recent Auth Audit Events</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                  <tr>
                    <th className="py-2">Time</th>
                    <th>Action</th>
                    <th>Actor</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.recent_auth_events || []).map((e) => (
                    <tr key={e.log_id} className="border-b border-slate-50" data-testid={`auth-diag-event-${e.log_id}`}>
                      <td className="py-2 text-xs text-slate-500">{e.created_at ? new Date(e.created_at).toLocaleString() : "—"}</td>
                      <td><code className="text-xs text-mg-medical font-mono">{e.action}</code></td>
                      <td className="text-xs text-slate-700">{e.actor?.email || e.actor_user_id || "—"}</td>
                      <td className="text-xs text-slate-600">{e.description || "—"}</td>
                    </tr>
                  ))}
                  {(!data.recent_auth_events || data.recent_auth_events.length === 0) && (
                    <tr><td colSpan="4" className="py-6 text-center text-slate-400 text-sm">No recent auth events.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function DiagRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-50 pb-1 last:border-0">
      <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</span>
      <span className="text-sm text-mg-navy break-all" data-testid={`auth-diag-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{value}</span>
    </div>
  );
}

function AuditLogDetailModal({ log, onClose }) {
  const [full, setFull] = useState(log);
  useEffect(() => {
    if (log?.log_id) {
      api.get(`/admin/audit-logs/${log.log_id}`).then((r) => setFull(r.data)).catch(() => {});
    }
  }, [log?.log_id]);
  const md = full.metadata || {};
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="audit-detail-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-mg-navy text-lg">Audit log detail</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-mg-navy text-xl" data-testid="audit-modal-close">×</button>
        </div>
        <div className="mt-3 space-y-2 text-sm">
          <Field2 label="Action" value={<code className="text-mg-medical font-mono">{full.action}</code>} />
          <Field2 label="Time" value={full.created_at ? new Date(full.created_at).toLocaleString() : "—"} />
          <Field2 label="Actor" value={full.actor ? `${full.actor.name || full.actor.email} (${full.actor.role})` : full.actor_user_id || "—"} />
          <Field2 label="Clinic" value={full.clinic ? `${full.clinic.name} (/${full.clinic.slug})` : full.clinic_id || "—"} />
          <Field2 label="Entity" value={`${full.entity_type || "—"} ${full.entity_id ? "· " + full.entity_id : ""}`} />
          <Field2 label="Description" value={full.description || "—"} />
          <Field2 label="IP" value={full.ip_address || "—"} />
          <Field2 label="Severity" value={full.severity || "—"} />
          <Field2 label="Status" value={full.status || "—"} />
          {md.old_values && (
            <Field2 label="Old values" value={<pre className="text-[10px] font-mono bg-red-50 text-red-900 p-2 rounded">{JSON.stringify(md.old_values, null, 2)}</pre>} />
          )}
          {md.new_values && (
            <Field2 label="New values" value={<pre className="text-[10px] font-mono bg-green-50 text-green-900 p-2 rounded">{JSON.stringify(md.new_values, null, 2)}</pre>} />
          )}
          {(md.bulk || md.reason || md.results) && (
            <Field2 label="Metadata" value={<pre className="text-[10px] font-mono bg-slate-50 p-2 rounded">{JSON.stringify(md, null, 2)}</pre>} />
          )}
        </div>
      </div>
    </div>
  );
}

function Field2({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 border-b border-slate-50 pb-2">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className="col-span-2 text-sm text-mg-navy break-all">{value}</div>
    </div>
  );
}
